'use client';

import { useEffect } from 'react';
import { Poppins } from 'next/font/google';

const poppins = Poppins({
  variable: '--font-poppins',
  subsets: ['latin'],
  weight: ['400', '500', '600', '700'],
});

interface GlobalErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function GlobalError({ error, reset }: GlobalErrorProps) {
  useEffect(() => {
    // Log the error to an error reporting service
    console.error('Global error:', error);
  }, [error]);

  return (
    <html lang="it" className="dark">
      <body className={`${poppins.variable} font-sans antialiased`}>
        <div
          className="min-h-screen flex flex-col items-center justify-center px-6 py-12 relative overflow-hidden"
          style={{
            background: 'linear-gradient(107.15deg, rgba(0, 0, 17, 1) 0%, rgba(0, 0, 44, 1) 70.47%, rgba(0, 0, 70, 1) 100%)',
          }}
        >
          {/* Background decorative elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div
              className="absolute top-1/3 left-1/3 w-96 h-96 rounded-full blur-3xl"
              style={{ background: 'rgba(220, 38, 38, 0.1)' }}
            />
            <div
              className="absolute bottom-1/3 right-1/3 w-80 h-80 rounded-full blur-3xl"
              style={{ background: 'rgba(139, 92, 246, 0.1)' }}
            />
          </div>

          {/* Logo fallback */}
          <a href="/" className="mb-12 relative z-10">
            <img
              src="/GeeksCreations Logo.svg"
              alt="GeeksCreations"
              width={120}
              height={54}
              className="h-14 w-auto"
            />
          </a>

          {/* Error content */}
          <div className="relative z-10 text-center max-w-2xl">
            {/* Critical error indicator */}
            <div className="relative mb-8 inline-block">
              <div className="text-[5rem] md:text-[7rem] select-none font-bold">
                <span
                  className="bg-clip-text text-transparent"
                  style={{
                    backgroundImage: 'linear-gradient(to right, #f87171, #8b5cf6, #f87171)',
                  }}
                >
                  CRASH
                </span>
              </div>
            </div>

            {/* Terminal-style error */}
            <div
              className="rounded-2xl mb-10 mx-auto max-w-lg p-[2px]"
              style={{
                background: 'linear-gradient(90.15deg, rgba(255, 255, 255, 0.1) 0.97%, rgba(255, 255, 255, 0.05) 50%, rgba(255, 255, 255, 0.1) 99.42%)',
              }}
            >
              <div
                className="p-6 rounded-2xl"
                style={{ background: 'rgba(0, 0, 0, 0.4)' }}
              >
                <div className="font-mono text-left space-y-2 text-sm">
                  <p style={{ color: '#f87171' }}>
                    <span style={{ color: '#64748b' }}>[FATAL]</span> GlobalException
                  </p>
                  <p style={{ color: '#cbd5e1' }} className="break-all">
                    <span style={{ color: '#64748b' }}>message:</span> A critical error occurred
                  </p>
                  {error.digest && (
                    <p style={{ color: '#64748b' }} className="text-xs">
                      <span>digest:</span> {error.digest}
                    </p>
                  )}
                  <p style={{ color: '#64748b' }} className="mt-2">
                    █
                  </p>
                </div>
              </div>
            </div>

            <p
              className="text-lg md:text-xl mb-10 leading-relaxed"
              style={{ color: '#cbd5e1' }}
            >
              Something went critically wrong. We&apos;re working on it.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={reset}
                className="inline-flex items-center justify-center gap-2.5 whitespace-nowrap rounded-full text-sm font-medium transition-all h-12 px-6 py-3 border-2 text-white"
                style={{
                  background: 'hsla(240, 100%, 63%, 1)',
                  borderColor: 'oklch(0.65 0.28 250)',
                  boxShadow: '0px 0px 30px 0px hsla(240, 100%, 63%, 0.9), 0px 0px 20px 0px hsla(240, 100%, 72%, 1) inset',
                }}
              >
                Try Again
              </button>
              <a
                href="/"
                className="inline-flex items-center justify-center gap-2.5 whitespace-nowrap rounded-full text-sm font-medium transition-all h-12 px-6 py-3 bg-transparent border-2 text-white"
                style={{
                  borderColor: 'oklch(0.96 0.05 280)',
                  boxShadow: '0px 0px 30px 0px hsla(240, 100%, 86%, 0.8), 0px 0px 20px 0px hsla(240, 100%, 86%, 1) inset',
                }}
              >
                Back to Home
              </a>
            </div>
          </div>

          {/* Decorative code lines */}
          <div
            className="absolute bottom-8 left-8 font-mono text-xs hidden md:block"
            style={{ color: '#475569' }}
          >
            <p>{'// Critical failure in root layout'}</p>
            <p>{'// Attempting recovery...'}</p>
          </div>
        </div>
      </body>
    </html>
  );
}

